import { useCallback, useSyncExternalStore } from 'react';

import { personalPrimaryColor } from '@/settings.ts';
import tintify from '@/utils/colors.ts';

import type { TailwindColorObject } from '@/types/colors.ts';

/**
 * Curated primary color presets for the personal theme picker.
 * Same hues/hex values as typent-social's AppearanceSection, for visual
 * consistency across both products.
 */
export interface PersonalThemeColor {
  key: string;
  label: string;
  hex: string;
}

export const PERSONAL_THEME_COLORS: readonly PersonalThemeColor[] = [
  { key: 'default', label: 'Default', hex: '#7C3AED' },
  { key: 'red', label: 'Red', hex: '#DC2626' },
  { key: 'orange', label: 'Orange', hex: '#EA580C' },
  { key: 'amber', label: 'Amber', hex: '#D97706' },
  { key: 'yellow', label: 'Yellow', hex: '#EAB308' },
  { key: 'lime', label: 'Lime', hex: '#65A30D' },
  { key: 'green', label: 'Green', hex: '#16A34A' },
  { key: 'emerald', label: 'Emerald', hex: '#059669' },
  { key: 'teal', label: 'Teal', hex: '#0D9488' },
  { key: 'cyan', label: 'Cyan', hex: '#0891B2' },
  { key: 'sky', label: 'Sky', hex: '#0284C7' },
  { key: 'blue', label: 'Blue', hex: '#3B82F6' },
  { key: 'indigo', label: 'Indigo', hex: '#6366F1' },
  { key: 'violet', label: 'Violet', hex: '#8B5CF6' },
  { key: 'purple', label: 'Purple', hex: '#A855F7' },
  { key: 'fuchsia', label: 'Fuchsia', hex: '#D946EF' },
  { key: 'pink', label: 'Pink', hex: '#EC4899' },
  { key: 'rose', label: 'Rose', hex: '#E11D48' },
  { key: 'iris', label: 'Iris', hex: '#6364FF' },
] as const;

/** Convert a full Tailwind shade object into `--color-primary-{shade}:R G B;` declarations. */
const paletteToCss = (palette: TailwindColorObject): string => {
  return Object.entries(palette).reduce((css, [shade, value]) => {
    const hex = value.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    if ([r, g, b].some(Number.isNaN)) return css;

    return css + `--color-primary-${shade}:${r} ${g} ${b};`;
  }, '');
};

/** Build the `:root` CSS block for a personal primary-color choice. */
export const buildPersonalThemeCss = (hex: string): string => {
  const palette = tintify(hex.replace('#', ''));
  return paletteToCss(palette);
};

const PERSONAL_THEME_EVENT = 'typent:personal-theme-change';

/** Persist the choice and notify any mounted `usePersonalPrimaryColorKey` subscribers. */
export const setPersonalPrimaryColor = (accountId: string, key: string): void => {
  personalPrimaryColor.set(accountId, key);
  window.dispatchEvent(new Event(PERSONAL_THEME_EVENT));
};

/** Reactively read the current personal primary-color key (`'default'` if unset). */
export const usePersonalPrimaryColorKey = (accountId: string | undefined): string => {
  const subscribe = useCallback((callback: () => void) => {
    window.addEventListener(PERSONAL_THEME_EVENT, callback);
    return () => window.removeEventListener(PERSONAL_THEME_EVENT, callback);
  }, []);

  const getSnapshot = useCallback(() => {
    return (accountId && personalPrimaryColor.get(accountId)) || 'default';
  }, [accountId]);

  return useSyncExternalStore(subscribe, getSnapshot);
};
